/**
 * Processing state. Mini PRD: "A processing state while the pipeline runs.
 * If it fails, the carer gets a retry and a 'talk to your care manager'
 * route, not a dead end."
 *
 * Runs the capture through the pipeline seam (pipeline.ts) - currently
 * simulated, later the live vision-LLM call. Fires the Mini PRD's
 * Document Uploaded / Document Capture Failed instrumentation events.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigation } from '@/hooks/useNavigationContext';
import { Stack, Typography } from '@mui/material';

import { Loader } from '@karehero/llama/components/atoms/Loader/Loader';
import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import FlowScreen from './components/FlowScreen';
import { palette } from './components/palette';
import { useCaptureFlow } from './CaptureFlowContext';
import { understand, CaptureFailedError } from './pipeline';

export const Processing = () => {
  const navigate = useNavigation();
  const { doc, method, surface, track } = useCaptureFlow();
  const [failReason, setFailReason] = useState<string | null>(null);
  // Guard against React StrictMode double-running the effect in dev,
  // which would double-fire instrumentation events.
  const ran = useRef(false);

  useEffect(() => {
    if (!doc) {
      navigate('/capture', { replace: true });
      return;
    }
    if (ran.current) return;
    ran.current = true;

    const startedAt = Date.now();
    understand({ fixture: doc })
      .then((result) => {
        track('Document Uploaded', {
          type: result.classification,
          capture_method: method,
          surface,
          page_count: result.pageCount,
          processing_ms: Date.now() - startedAt,
        });
        navigate('/capture/result', { replace: true });
      })
      .catch((error: unknown) => {
        const reason =
          error instanceof CaptureFailedError
            ? error.reason
            : 'Something went wrong while reading the document.';
        track('Document Capture Failed', { type: 'unknown', reason });
        setFailReason(reason);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  if (!doc) return null;

  if (failReason) {
    return (
      <FlowScreen backTo='/capture'>
        <Stack
          spacing={2}
          sx={{ alignItems: 'center', pt: 6, textAlign: 'center' }}
        >
          <Icon icon='exclamation-triangle' fontSize={36} />
          <Typography variant='h5'>We couldn't read that document</Typography>
          <Typography sx={{ color: palette.mutedText, fontSize: 14 }}>
            {failReason}
          </Typography>
          <Stack spacing={1.5} sx={{ width: '100%', pt: 2 }}>
            <Button
              ariaLabel='Try again'
              variant='primary'
              isFullWidth
              onPress={() => {
                track('Capture Retry Tapped (proposed)', {
                  reason: failReason ?? 'unknown',
                });
                navigate('/capture');
              }}
            >
              Try again
            </Button>
            <Button
              ariaLabel='Talk to your care manager'
              variant='secondary'
              isFullWidth
              onPress={() => navigate('/help')}
            >
              Talk to your care manager
            </Button>
          </Stack>
        </Stack>
      </FlowScreen>
    );
  }

  return (
    <FlowScreen hideBack>
      <Stack
        spacing={2}
        sx={{ alignItems: 'center', pt: 4, textAlign: 'center' }}
      >
        <Loader />
        <Typography variant='h5'>Keni is reading your document</Typography>
        <Typography sx={{ color: palette.mutedText, fontSize: 14 }}>
          {doc.pageCount > 1
            ? `Going through all ${doc.pageCount} pages. This usually takes a few seconds.`
            : 'This usually takes a few seconds.'}
        </Typography>
      </Stack>
    </FlowScreen>
  );
};

export default Processing;
